import type { ItemImageSources } from "@/lib/catalog-item-image";
import { rearmImageIfAlreadyFailed } from "@/lib/rearm-image";

/**
 * One item image as a `<picture>` — the rendition ladder as `<source>`s, with
 * the original behind them as the `<img>`'s own `src`.
 *
 * The composition is the same at all three sizes this app draws an item image
 * at — the catalog's box, the details gallery's main slot, the lightbox — and
 * what differs between them is geometry: the `sizes` the browser resolves the
 * `w` descriptors against, and the classes on the `<img>`. Those are the props;
 * the ordering, the `type`s and the fallback are not, because getting any of
 * them wrong is silent (a `<source type>` no browser matches simply falls
 * through to the full-size original, at every slot on the page).
 *
 * Takes the `ItemImageSources` bundle whole rather than `src` and `sources`
 * apart: `itemImageSources()` returns them together because they are one
 * answer, and splitting them at every call site was the shape that let a caller
 * pass one image's ladder with another's original.
 *
 * **The `<img>` re-arms itself** (`rearmImageIfAlreadyFailed`, #539). Atlas
 * server-renders, so a picture can finish failing before React attaches
 * `onError` and the fallback never hears about it. Wiring the arm here rather
 * than at the call sites is what gets it to every slot that goes through this
 * component — which, since the Add Variant tile stopped hand-rolling its own
 * `<picture>`, is every item-image slot but one: the bare `<img>` in
 * `image-upload-field.tsx`. `onError`'s contract and `useItemImageFailure`'s
 * signature are unchanged.
 */
interface ItemPictureProps {
  /** The bundle from `itemImageSources()` — the ladder and the original. */
  sources: ItemImageSources;
  alt: string;
  /**
   * How wide the browser should assume this slot is.
   *
   * Required, and never defaulted: without it the browser assumes `100vw` and
   * pulls the widest rung into whatever the slot actually is.
   */
  sizes: string;
  /** Classes on the `<img>` — the picture's own geometry. */
  className?: string;
  /** Classes on the `<picture>` element, where a caller needs it to lay out. */
  pictureClassName?: string;
  loading?: "lazy" | "eager";
  onError?: () => void;
  "data-testid"?: string;
}

export function ItemPicture({
  sources,
  alt,
  sizes,
  className,
  pictureClassName,
  loading,
  onError,
  "data-testid": testId,
}: ItemPictureProps) {
  return (
    <picture data-testid={testId} className={pictureClassName}>
      {sources.sources.map((source) => (
        <source key={source.type} type={source.type} srcSet={source.srcSet} sizes={sizes} />
      ))}
      <img
        // The bare function, deliberately: a ref re-runs when its identity
        // changes, so an arrow here would re-arm every render and a genuinely
        // broken picture would re-fetch forever.
        ref={rearmImageIfAlreadyFailed}
        src={sources.src}
        alt={alt}
        className={className}
        loading={loading}
        onError={onError}
      />
    </picture>
  );
}
